import type { AideCatalogue, AidePage } from '@oasis/contracts';
import * as RadixDialog from '@radix-ui/react-dialog';
import { CircleHelp, X } from 'lucide-react';
import { useEffect, useState, type ReactElement } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';

import { useApiQuery } from '../api.js';
import { ErrorMessage, Loading } from '../design/components.js';

import { AideContenu } from './AideContenu.js';
import { pageDeLaRoute, toucheAide } from './logique.js';

/**
 * Panneau « Aide » de l'écran courant : ouvert par le bouton de l'en-tête ou la touche `?`.
 * Contenu servi par `/ui/aide` (pages que la personne peut utiliser), choisi d'après la
 * route ; à défaut, renvoi vers le centre d'aide et sa recherche.
 */

/** Corps du panneau, sans dépendance au chargement (rendu testable). */
export function AidePanneauContenu({
  page,
  onFermer,
}: {
  page: AidePage | undefined;
  onFermer?: () => void;
}): ReactElement {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  return (
    <div className="o-aide-panneau__corps">
      {page === undefined ? (
        <p>
          Pas encore de page d'aide pour cet écran. Le centre d'aide présente tout ce que vous
          pouvez utiliser.
        </p>
      ) : (
        <AideContenu page={page} />
      )}
      <form
        className="o-aide-panneau__recherche"
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          onFermer?.();
          void navigate(`/aide?q=${encodeURIComponent(q)}`);
        }}
      >
        <label htmlFor="aide-panneau-q" className="o-visually-hidden">
          Rechercher dans l'aide
        </label>
        <input
          id="aide-panneau-q"
          className="o-input"
          type="search"
          placeholder="Rechercher dans l'aide…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
          }}
        />
      </form>
      <p className="o-row">
        <Link to="/aide" onClick={onFermer}>
          Centre d'aide
        </Link>
        <Link to="/aide/nouveautes" onClick={onFermer}>
          Nouveautés
        </Link>
        <span className="o-hint">
          Touche <kbd>?</kbd> pour ouvrir l'aide
        </span>
      </p>
    </div>
  );
}

function PanneauCharge({ onFermer }: { onFermer: () => void }): ReactElement {
  const { pathname } = useLocation();
  const q = useApiQuery<AideCatalogue>('/ui/aide');
  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorMessage error={q.error} />;
  return <AidePanneauContenu page={pageDeLaRoute(q.data.pages, pathname)} onFermer={onFermer} />;
}

/** Bouton de l'en-tête et raccourci `?` : point d'entrée unique de l'aide dans la coque. */
export function AideBouton(): ReactElement {
  const [ouvert, setOuvert] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const surTouche = (e: KeyboardEvent): void => {
      if (!toucheAide(e)) return;
      e.preventDefault();
      setOuvert(true);
    };
    window.addEventListener('keydown', surTouche);
    return () => {
      window.removeEventListener('keydown', surTouche);
    };
  }, []);

  // Changement d'écran (lien suivi depuis le panneau) : le panneau se ferme.
  useEffect(() => {
    setOuvert(false);
  }, [pathname]);

  return (
    <RadixDialog.Root open={ouvert} onOpenChange={setOuvert}>
      <RadixDialog.Trigger asChild>
        <button
          type="button"
          className="o-button o-button--ghost o-icon-button"
          aria-label="Aide sur cet écran (touche ?)"
          title="Aide (touche ?)"
        >
          <CircleHelp aria-hidden="true" />
        </button>
      </RadixDialog.Trigger>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="o-overlay" />
        <RadixDialog.Content className="o-aide-panneau" aria-describedby={undefined}>
          <div className="o-aide-panneau__entete">
            <RadixDialog.Title asChild>
              <h2>Aide</h2>
            </RadixDialog.Title>
            <RadixDialog.Close asChild>
              <button
                type="button"
                className="o-button o-button--ghost o-icon-button"
                aria-label="Fermer l'aide"
              >
                <X aria-hidden="true" />
              </button>
            </RadixDialog.Close>
          </div>
          {ouvert && (
            <PanneauCharge
              onFermer={() => {
                setOuvert(false);
              }}
            />
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
